/**
 * Notification Manager — Hybrid notification routing engine.
 *
 * Decides whether to show:
 *   - In-app toast/banner/modal (when app is in foreground)
 *   - Native push notification (when app is in background — handled by SW)
 *
 * Also handles:
 *   - Deduplication (tag-based, within a time window)
 *   - Frequency limiting (max N notifications per type per minute)
 *   - User preference checks
 *   - Analytics tracking (sent, displayed, clicked, dismissed)
 *   - Remind-me-later scheduling
 */

import apiClient from './apiClient';

// ── Deduplication ──
const recentTags = new Map(); // tag → timestamp
const DEDUP_WINDOW_MS = 10_000; // 10 seconds

// ── Rate limiting ──
const rateLimitBuckets = new Map(); // type → [timestamps]
const MAX_PER_TYPE_PER_MINUTE = 5;

// ── Analytics event queue ──
const analyticsQueue = [];
let flushTimer = null;

/**
 * Notification categories that users can toggle.
 */
export const NOTIFICATION_CATEGORIES = {
  class: { label: 'Live Classes', description: 'Class start, end, and reminders', default: true },
  assignment: { label: 'Assignments', description: 'New assignments and deadlines', default: true },
  chat: { label: 'Chat Messages', description: 'New messages in batch chat', default: true },
  payment: { label: 'Payments', description: 'Payment receipts and reminders', default: true },
  general: { label: 'General', description: 'Announcements and updates', default: true },
};

/**
 * Map notification types to categories.
 *
 * ⚠️ Keep this in step with `vc_be/utils/notificationGate.js`, which enforces
 * the same preferences for push and email. The two had drifted: `whatsapp_message`
 * was missing here (so it fell through to `general` while the server filed it
 * under `chat`), and `batch_update` was `general` here and `class` there — one
 * toggle meaning two different things depending on the channel.
 */
const TYPE_TO_CATEGORY = {
  class_started: 'class',
  class_ended: 'class',
  batch_update: 'class',
  batch_completed: 'class',
  chat_message: 'chat',
  whatsapp_message: 'chat',
  assignment_posted: 'assignment',
  assignment_graded: 'assignment',
  assignment_returned: 'assignment',
  payment_received: 'payment',
  payment_settled: 'payment',
  payout_completed: 'payment',
  fee_reminder: 'payment',
  general: 'general',
  new_enquiry: 'general',
  ai_report: 'general',
  ai_nudge: 'general',
};

// ── Audience ──
//
// Some notifications are staff business: a lead's WhatsApp message, a new
// enquiry, a ringing call, an integration warning. The server routes those to
// staff-only socket rooms; this is the client-side backstop, so a student or
// instructor tab never renders one that reaches it anyway — a misrouted emit,
// or a backend still mid-deploy.
//
// A type that carries no audience of its own (the generic `general` that
// follow-up and invoice alerts use) can only be scoped server-side.
const STAFF_USERTYPES = ['admin', 'superadmin', 'tenantadmin'];

const STAFF_ONLY_TYPES = new Set([
  'new_enquiry', 'enquiry_new',     // a lead's name, phone and email
  'incoming_call', 'missed_call',   // the CRM softphone's screen-pop
  'integration_degraded',           // WhatsApp / Meta connection health
  'marketplace_published',
]);

export function isStaffUser(usertype) {
  return STAFF_USERTYPES.includes(usertype);
}

export function isStaffOnlyNotification(type) {
  if (typeof type !== 'string') return false;
  // Every whatsapp_* event belongs to the inbox, and that is staff-only.
  return type.startsWith('whatsapp_') || STAFF_ONLY_TYPES.has(type);
}

// ── User preference helpers ──
//
// Source of truth lives on the server (`NotificationPreference` collection,
// see `vc_be/models/NotificationPreference.js`). The server enforces the
// preferences for push (web + FCM) and any other channel-aware dispatch.
//
// Locally we keep a synchronous flattened cache in localStorage so that the
// foreground in-app toast / sound gating can run without an awaitable lookup
// every time a socket event arrives. The cache shape is `{ class: bool, ... }`
// — derived from the server's `inApp` channel for each category.

function getPreferencesKey() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return `notif-prefs-${user._id || user.id || 'anon'}`;
  } catch {
    return 'notif-prefs-anon';
  }
}

function defaultsFlat() {
  const defaults = {};
  for (const [key, cat] of Object.entries(NOTIFICATION_CATEGORIES)) {
    defaults[key] = cat.default;
  }
  return defaults;
}

export function getNotificationPreferences() {
  try {
    const stored = localStorage.getItem(getPreferencesKey());
    if (stored) return JSON.parse(stored);
  } catch { /* use defaults */ }
  return defaultsFlat();
}

/**
 * Persist the flat in-app cache only (used by `routeNotification`).
 * Does NOT contact the server — for that use `saveNotificationPreferencesToServer`.
 */
export function setNotificationPreferences(prefs) {
  localStorage.setItem(getPreferencesKey(), JSON.stringify(prefs));
}

function flattenServerPrefs(serverPrefs) {
  const flat = defaultsFlat();
  if (!serverPrefs) return flat;
  for (const key of Object.keys(flat)) {
    const cat = serverPrefs[key];
    if (!cat) continue;
    // Treat the in-app channel as the foreground gate; if any one of the 4
    // channels is enabled we still want the bell entry, so the in-app toast
    // gate keys off `inApp` specifically.
    if (typeof cat.inApp === 'boolean') flat[key] = cat.inApp;
  }
  return flat;
}

/**
 * Fetch preferences from the server and refresh the local cache.
 * Returns the server-shaped prefs `{ class: { inApp, push, email, whatsapp }, ... }`.
 */
export async function loadNotificationPreferencesFromServer() {
  try {
    const { data } = await apiClient.get('/notifications/preferences');
    setNotificationPreferences(flattenServerPrefs(data?.prefs));
    return data?.prefs || null;
  } catch (err) {
    // Stay quiet — fall back to whatever cache we have
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[notificationManager] Failed to load prefs from server:', err?.message);
    }
    return null;
  }
}

/**
 * Save preferences to the server and refresh the local cache.
 * Accepts the server-shaped prefs `{ class: { inApp, push, email, whatsapp }, ... }`.
 */
export async function saveNotificationPreferencesToServer(serverShapedPrefs) {
  const { data } = await apiClient.put('/notifications/preferences', { prefs: serverShapedPrefs });
  setNotificationPreferences(flattenServerPrefs(data?.prefs));
  return data?.prefs || serverShapedPrefs;
}

function isCategoryEnabled(type) {
  const category = TYPE_TO_CATEGORY[type] || 'general';
  const prefs = getNotificationPreferences();
  return prefs[category] !== false;
}

// ── Deduplication ──

function isDuplicate(tag) {
  if (!tag) return false;
  const now = Date.now();
  // Clean expired entries
  for (const [key, ts] of recentTags) {
    if (now - ts > DEDUP_WINDOW_MS) recentTags.delete(key);
  }
  if (recentTags.has(tag)) return true;
  recentTags.set(tag, now);
  return false;
}

// ── Rate limiting ──

function isRateLimited(type) {
  const now = Date.now();
  const bucket = rateLimitBuckets.get(type) || [];
  // Keep only last 60s
  const filtered = bucket.filter((ts) => now - ts < 60_000);
  if (filtered.length >= MAX_PER_TYPE_PER_MINUTE) return true;
  filtered.push(now);
  rateLimitBuckets.set(type, filtered);
  return false;
}

// ── Foreground detection ──

export function isAppVisible() {
  return document.visibilityState === 'visible' && document.hasFocus();
}

// ── Analytics ──

function queueAnalyticsEvent(event) {
  analyticsQueue.push({ ...event, timestamp: new Date().toISOString() });
  // Flush every 30 seconds or when queue hits 20
  if (analyticsQueue.length >= 20) {
    flushAnalytics();
  } else if (!flushTimer) {
    flushTimer = setTimeout(flushAnalytics, 30_000);
  }
}

async function flushAnalytics() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (analyticsQueue.length === 0) return;
  const batch = analyticsQueue.splice(0);
  try {
    const { default: apiClient } = await import('./apiClient');
    await apiClient.post('/notifications/analytics', { events: batch });
  } catch {
    // Re-queue on failure (max 50 to prevent memory leak)
    if (analyticsQueue.length < 50) {
      analyticsQueue.push(...batch);
    }
  }
}

export function trackNotification(notifId, action, meta = {}) {
  queueAnalyticsEvent({
    notificationId: notifId || `notif-${Date.now()}`,
    action, // 'sent' | 'displayed' | 'clicked' | 'dismissed' | 'remind_later'
    ...meta,
  });
}

// ── Remind Me Later ──

const remindTimers = new Map();

export function remindMeLater(notification, delayMs = 5 * 60 * 1000) {
  const id = notification.tag || notification._id || `remind-${Date.now()}`;
  // Cancel any existing reminder for this notification
  if (remindTimers.has(id)) clearTimeout(remindTimers.get(id));
  const timer = setTimeout(() => {
    remindTimers.delete(id);
    // Re-dispatch through the notification system
    window.dispatchEvent(
      new CustomEvent('vc-notification', { detail: { ...notification, isReminder: true } })
    );
  }, delayMs);
  remindTimers.set(id, timer);
  trackNotification(id, 'remind_later', { delayMs });
}

/**
 * The key two delivery paths must agree on to recognise the same event.
 *
 * One class start arrives twice — over the socket, and as a push the service
 * worker forwards — and each carries a tag of its own making (`class-<id>` vs
 * `class-started-<id>`), so neither side's tag can be the key. When the event
 * names a batch, that is what identifies it; otherwise fall back to the tag it
 * brought.
 */
export function eventKey(type, { batchId, tag } = {}) {
  if (batchId) return `${type}-${batchId}`;
  return tag || `${type}-${Date.now()}`;
}

/**
 * Should this notification be shown in the app right now?
 *
 * The gate on its own — preferences, the dedup window, the rate limit — without
 * the analytics event, so a caller that records its own `displayed` doesn't
 * count the same notification twice. `routeNotification` is this plus the
 * foreground/background decision.
 */
export function shouldShowInApp(notification) {
  const type = notification.type || 'general';
  const tag = eventKey(type, notification);

  if (!isCategoryEnabled(type)) {
    return { show: false, tag, reason: 'category_disabled' };
  }
  if (isDuplicate(tag)) {
    return { show: false, tag, reason: 'duplicate' };
  }
  if (isRateLimited(type)) {
    return { show: false, tag, reason: 'rate_limited' };
  }
  return { show: true, tag };
}

// ── Main routing function ──

/**
 * Process an incoming notification and decide how to display it.
 * Returns: { show: boolean, method: 'in-app' | 'push' | 'none', reason?: string }
 *
 * @param {Object} notification - The notification payload
 * @param {string} notification.type - Notification type (class_started, chat_message, etc.)
 * @param {string} notification.title - Title text
 * @param {string} notification.message - Body text
 * @param {string} [notification.tag] - Dedup tag
 * @param {Object} [notification.data] - Extra data for deep linking
 */
export function routeNotification(notification) {
  const type = notification.type || 'general';

  // 1-3. Preferences, deduplication, rate limiting.
  const { show, tag, reason } = shouldShowInApp(notification);
  if (!show) {
    return { show: false, method: 'none', reason };
  }

  // 4. Foreground → in-app, Background → push (SW handles it)
  if (isAppVisible()) {
    trackNotification(tag, 'displayed', { method: 'in-app', type });
    return { show: true, method: 'in-app' };
  }

  // Background — SW will show native push
  trackNotification(tag, 'displayed', { method: 'push', type });
  return { show: true, method: 'push' };
}
